import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiShield, FiAlertTriangle } from 'react-icons/fi';

const C = { coral: '#E3425C', charcoal: '#1C1C1E' };

export default function TermsConditions() {
  const sectionTitle = {
    fontFamily: "'DM Serif Display', serif",
    fontSize: '24px',
    color: C.charcoal,
    marginBottom: '16px',
    marginTop: '48px',
  };

  const paragraph = {
    fontSize: '15px',
    color: '#555',
    lineHeight: 1.85,
    marginBottom: '14px',
  };

  const listStyle = {
    fontSize: '15px',
    color: '#555',
    lineHeight: 1.85,
    paddingLeft: '24px',
    marginBottom: '14px',
  };

  const card = {
    padding: '32px',
    background: 'white',
    borderRadius: '16px',
    border: '1px solid rgba(0,0,0,0.05)',
    marginBottom: '20px',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#FAFAF9', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Serif+Display&display=swap');`}</style>

      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(0,0,0,0.05)', padding: '14px 0' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: C.charcoal, fontSize: '14px', fontWeight: 500 }}>
            <FiArrowLeft size={16} />
            <img src="/ggr-logo-deck.png" alt='GGR' style={{ height: '50px', width: 'auto' }} />
          </Link>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Link to="/register" style={{ padding: '9px 20px', borderRadius: '8px', background: C.coral, color: 'white', textDecoration: 'none', fontSize: '13px', fontWeight: 600 }}>Get Started</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ padding: '80px 0 40px', textAlign: 'center' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto', padding: '0 28px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 16px', borderRadius: '20px', background: `${C.coral}10`, color: C.coral, fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '20px' }}>
            <FiShield size={12} /> Legal
          </div>
          <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 400, color: C.charcoal, lineHeight: 1.15, marginBottom: '20px' }}>
            Terms &amp; Conditions
          </h1>
          <p style={{ fontSize: '16px', color: '#888', lineHeight: 1.7 }}>
            Please read these terms carefully before using Grand Gather Registry. By accessing or using our platform, you agree to be bound by these terms.
          </p>
          <p style={{ fontSize: '13px', color: '#aaa', marginTop: '12px' }}>Last updated: June 2026</p>
        </div>
      </section>

      {/* Content */}
      <section style={{ padding: '20px 0 80px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 28px' }}>

          {/* 1. Welcome & Acceptance */}
          <h2 style={sectionTitle}>1. Welcome &amp; Acceptance of Terms</h2>
          <div style={card}>
            <p style={paragraph}>
              Welcome to Grand Gather Registry ("GGR", "we", "us", "our"). GGR is a South African celebration planning platform operated by Grand Gather Registry (Pty) Ltd. By accessing, browsing, or using our website, mobile application, or any of our services, you ("User", "you", "your") acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
            </p>
            <p style={paragraph}>
              If you do not agree to these terms, please do not use our platform. We reserve the right to update these terms at any time. Continued use of GGR after changes constitutes acceptance of the revised terms. We will notify registered users of material changes via email.
            </p>
          </div>

          {/* 2. Platform Description */}
          <h2 style={sectionTitle}>2. Platform Description</h2>
          <div style={card}>
            <p style={paragraph}>
              Grand Gather Registry is a comprehensive celebration planning platform designed for the South African market. Our services include:
            </p>
            <ul style={listStyle}>
              <li><strong>Event Planning Tools</strong> — Create and manage event pages for weddings, birthdays, lobola ceremonies, umembeso, baby showers, funerals, and other celebrations.</li>
              <li><strong>Gift Registry</strong> — Set up digital gift registries linked to retail stores, allowing guests to purchase gifts using unique purchase codes.</li>
              <li><strong>Vendor Marketplace</strong> — Browse, compare, and book verified vendors including caterers, decorators, photographers, DJs, venues, and more.</li>
              <li><strong>Digital Invitations</strong> — Send beautiful invitations and manage RSVPs digitally.</li>
              <li><strong>GiGi AI Assistant</strong> — Our AI-powered planning assistant to help you organise your celebration.</li>
            </ul>
          </div>

          {/* 3. User Accounts */}
          <h2 style={sectionTitle}>3. User Accounts</h2>
          <div style={card}>
            <p style={paragraph}>
              To access certain features, you must register for an account. When creating an account, you agree to:
            </p>
            <ul style={listStyle}>
              <li>Provide accurate, current, and complete information during registration.</li>
              <li>Maintain the security of your password and accept responsibility for all activities under your account.</li>
              <li>Notify us immediately of any unauthorized use of your account.</li>
              <li>Not create multiple accounts for the same person or entity.</li>
              <li>Not share your account credentials with third parties.</li>
            </ul>
            <p style={paragraph}>
              GGR reserves the right to suspend or terminate accounts that violate these terms, provide false information, or engage in fraudulent activity.
            </p>
          </div>

          {/* 4. Event Hosting */}
          <h2 style={sectionTitle}>4. Event Hosting</h2>
          <div style={card}>
            <p style={paragraph}>
              GGR offers event hosting capabilities with the following tiers:
            </p>
            <ul style={listStyle}>
              <li><strong>Free Tier (Basic)</strong> — Create event pages with essential features including digital invitations, basic gift registry, and RSVP management at no cost.</li>
              <li><strong>Premium Tier (Coming Soon)</strong> — Enhanced features including advanced customisation, priority vendor matching, and premium templates. Pricing: <strong>R49/month</strong> or <strong>R99 per event</strong>.</li>
            </ul>
            <p style={paragraph}>
              <strong>Event Page Expiry:</strong> All event pages will automatically expire and become inaccessible <strong>3 days after the event date</strong>. Hosts may download their event data (guest lists, photos, messages) before expiry. GGR is not obligated to retain event data after the expiry period.
            </p>
          </div>

          {/* 5. Gift Registry & Purchase Codes */}
          <h2 style={sectionTitle}>5. Gift Registry &amp; Purchase Codes</h2>
          <div style={card}>
            <p style={paragraph}>
              Our gift registry system allows guests to purchase gifts from retail stores using unique purchase codes. The following terms apply:
            </p>
            <ul style={listStyle}>
              <li><strong>Code Generation</strong> — Unique gift codes are generated for each retail store purchase facilitated through GGR.</li>
              <li><strong>Platform Commission</strong> — All gift code transactions are subject to a <strong>5% platform commission</strong>, which is deducted from the transaction value.</li>
              <li><strong>Lost Codes</strong> — If a purchase code is lost, it can be recovered by contacting us via the registered email address associated with your account.</li>
              <li><strong>Retail Store Pricing</strong> — GGR is <strong>not responsible</strong> for retail store pricing, stock availability, product quality, or changes to retail store policies. We act as a facilitator, not a retailer.</li>
              <li><strong>Code Validity</strong> — Purchase codes are valid for the duration of the event and up to 3 days after the event date.</li>
            </ul>
          </div>

          {/* 6. Vendor Terms - PROMINENT */}
          <h2 style={{ ...sectionTitle, marginTop: '56px' }}>6. Vendor Terms &amp; Commission Structure</h2>
          <div style={{
            padding: '36px',
            background: 'linear-gradient(135deg, #FFF5F6 0%, #FFF0F1 100%)',
            borderRadius: '20px',
            border: `2px solid ${C.coral}`,
            marginBottom: '20px',
            position: 'relative',
            overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              background: C.coral,
              color: 'white',
              padding: '10px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: '0.5px',
            }}>
              <FiAlertTriangle size={15} />
              IMPORTANT: ALL VENDORS MUST READ AND UNDERSTAND THIS SECTION BEFORE SIGNING UP
            </div>

            <div style={{ marginTop: '44px' }}>
              <p style={{ ...paragraph, fontWeight: 500, color: C.charcoal }}>
                By registering as a vendor on Grand Gather Registry, you agree to pay a commission on every transaction facilitated through our platform. This commission is how GGR sustains its operations and continues to connect you with customers.
              </p>

              <h3 style={{ fontFamily: "'DM Serif Display', serif", fontSize: '20px', color: C.charcoal, marginTop: '28px', marginBottom: '16px' }}>
                Commission Tiers
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                {/* Starter Tier */}
                <div style={{ padding: '24px', background: 'white', borderRadius: '14px', border: '1px solid rgba(0,0,0,0.06)', textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', color: '#888', marginBottom: '8px' }}>Starter</div>
                  <div style={{ fontSize: '36px', fontWeight: 700, color: C.charcoal, lineHeight: 1 }}>8%</div>
                  <div style={{ fontSize: '13px', color: '#888', marginTop: '4px', marginBottom: '12px' }}>per transaction</div>
                  <div style={{ fontSize: '13px', color: '#666', padding: '8px 12px', background: '#F8F8F8', borderRadius: '8px' }}>
                    <strong>FREE</strong> &mdash; no monthly fee
                  </div>
                  <div style={{ fontSize: '12px', color: '#aaa', marginTop: '8px' }}>5 portfolio images &middot; 1x loyalty points</div>
                </div>

                {/* Pro Tier */}
                <div style={{ padding: '24px', background: 'white', borderRadius: '14px', border: `2px solid ${C.coral}`, textAlign: 'center', position: 'relative' }}>
                  <div style={{ position: 'absolute', top: '-1px', left: '50%', transform: 'translateX(-50%)', background: C.coral, color: 'white', fontSize: '10px', fontWeight: 700, padding: '3px 12px', borderRadius: '0 0 8px 8px', letterSpacing: '0.5px' }}>POPULAR</div>
                  <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', color: '#888', marginBottom: '8px' }}>Pro</div>
                  <div style={{ fontSize: '36px', fontWeight: 700, color: C.coral, lineHeight: 1 }}>3%</div>
                  <div style={{ fontSize: '13px', color: '#888', marginTop: '4px', marginBottom: '12px' }}>per transaction</div>
                  <div style={{ fontSize: '13px', color: '#666', padding: '8px 12px', background: '#FFF5F6', borderRadius: '8px' }}>
                    <strong>R199</strong>/month subscription
                  </div>
                  <div style={{ fontSize: '12px', color: '#aaa', marginTop: '8px' }}>15 portfolio images &middot; 2x loyalty points &middot; Verified badge</div>
                </div>

                {/* Premium Tier */}
                <div style={{ padding: '24px', background: 'white', borderRadius: '14px', border: '1px solid rgba(0,0,0,0.06)', textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', color: '#888', marginBottom: '8px' }}>Premium</div>
                  <div style={{ fontSize: '36px', fontWeight: 700, color: C.charcoal, lineHeight: 1 }}>0%</div>
                  <div style={{ fontSize: '13px', color: '#888', marginTop: '4px', marginBottom: '12px' }}>commission-free</div>
                  <div style={{ fontSize: '13px', color: '#666', padding: '8px 12px', background: '#F8F8F8', borderRadius: '8px' }}>
                    <strong>R499</strong>/month subscription
                  </div>
                  <div style={{ fontSize: '12px', color: '#aaa', marginTop: '8px' }}>Unlimited images &middot; 3x loyalty points &middot; Top Vendor badge</div>
                </div>
              </div>

              <div style={{ padding: '16px 20px', background: 'rgba(227,66,92, 0.08)', borderRadius: '10px', borderLeft: `4px solid ${C.coral}`, marginBottom: '20px' }}>
                <p style={{ fontSize: '14px', color: C.charcoal, lineHeight: 1.7, margin: 0, fontWeight: 500 }}>
                  All new vendors start on the <strong>Starter (FREE) tier</strong> at 8% commission. To benefit from reduced commission rates and additional features, upgrade to the <strong>Pro tier (R199/month, 3% commission)</strong> or the <strong>Premium tier (R499/month, 0% commission)</strong>. Vendors earn loyalty points on every booking — redeemable for platform credits, featured listings, and rewards.
                </p>
              </div>

              <h3 style={{ fontFamily: "'DM Serif Display', serif", fontSize: '20px', color: C.charcoal, marginTop: '28px', marginBottom: '16px' }}>
                Additional Vendor Obligations
              </h3>
              <ul style={listStyle}>
                <li>GGR reserves the right to <strong>adjust commission rates with 30 days written notice</strong> to all affected vendors.</li>
                <li>Vendors must maintain <strong>accurate and up-to-date business information</strong>, including service descriptions, pricing, availability, and contact details.</li>
                <li>Vendors must respond to booking enquiries within a reasonable timeframe.</li>
                <li>Vendors are responsible for the quality and delivery of their services. GGR acts as a marketplace facilitator and is not a party to vendor-client service agreements.</li>
                <li>Falsifying reviews, bookings, or business information may result in immediate account suspension.</li>
                <li><strong>Identity Verification for Withdrawals:</strong> All vendors (and hosts with wallets) must verify their South African ID number before withdrawing funds from the platform. This is a fraud-prevention measure and a regulatory compliance requirement. The 13-digit SA ID number is validated against the national format and must be unique to your account.</li>
              </ul>
            </div>
          </div>

          {/* 7. Sponsor & Advertising */}
          <h2 style={sectionTitle}>7. Sponsorship &amp; Advertising</h2>
          <div style={card}>
            <p style={paragraph}>
              GGR offers advertising and sponsorship opportunities on its platform. The following terms apply to all sponsors and advertisers:
            </p>
            <ul style={listStyle}>
              <li><strong>Payment</strong> — Ad placements are billed and paid on a monthly basis.</li>
              <li><strong>Approval</strong> — GGR reserves the right to approve or reject any sponsor or advertisement at its sole discretion. Ads must comply with South African advertising standards and must not contain offensive, misleading, or illegal content.</li>
              <li><strong>Performance Tracking</strong> — Click and impression tracking data is provided to sponsors for transparency and campaign optimisation.</li>
              <li><strong>Cancellation</strong> — Sponsors may cancel their advertising arrangement with 30 days written notice. No pro-rata refunds will be issued for the current billing period.</li>
            </ul>
          </div>

          {/* 8. Privacy & POPIA */}
          <h2 style={sectionTitle}>8. Privacy &amp; POPIA Compliance</h2>
          <div style={card}>
            <p style={paragraph}>
              Grand Gather Registry is committed to protecting your privacy in accordance with the <strong>Protection of Personal Information Act (POPIA)</strong> of South Africa.
            </p>

            <h4 style={{ fontSize: '16px', fontWeight: 600, color: C.charcoal, marginTop: '20px', marginBottom: '10px' }}>Personal Data We Collect</h4>
            <ul style={listStyle}>
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Business information (for vendors)</li>
              <li>Event details and guest information (for hosts)</li>
            </ul>

            <h4 style={{ fontSize: '16px', fontWeight: 600, color: C.charcoal, marginTop: '20px', marginBottom: '10px' }}>How We Use Your Data</h4>
            <ul style={listStyle}>
              <li><strong>Platform Operation</strong> — To provide, maintain, and improve our services.</li>
              <li><strong>Communication</strong> — To send service-related notifications, updates, and promotional content (with your consent).</li>
              <li><strong>Analytics</strong> — To understand usage patterns and improve the user experience.</li>
              <li><strong>Legal Compliance</strong> — To comply with applicable laws and regulations.</li>
            </ul>

            <h4 style={{ fontSize: '16px', fontWeight: 600, color: C.charcoal, marginTop: '20px', marginBottom: '10px' }}>Your Rights</h4>
            <ul style={listStyle}>
              <li>You may <strong>request access</strong> to the personal data we hold about you.</li>
              <li>You may <strong>request deletion</strong> of your personal data by contacting us at support@grandgatherregistry.co.za.</li>
              <li>You may <strong>withdraw consent</strong> for marketing communications at any time.</li>
              <li>You may <strong>lodge a complaint</strong> with the Information Regulator of South Africa if you believe your data rights have been violated.</li>
            </ul>

            <h4 style={{ fontSize: '16px', fontWeight: 600, color: C.charcoal, marginTop: '20px', marginBottom: '10px' }}>Data Sharing &amp; Cookies</h4>
            <p style={paragraph}>
              Your personal data is <strong>never sold to third parties</strong>. We may share limited data with service providers (e.g., payment processors) strictly for the purpose of operating our platform. We use cookies for session management and to enhance your browsing experience. By using GGR, you consent to our use of cookies.
            </p>
          </div>

          {/* 9. Payment Terms */}
          <h2 style={sectionTitle}>9. Payment Terms</h2>
          <div style={card}>
            <ul style={listStyle}>
              <li>All monetary amounts displayed on GGR are in <strong>South African Rand (ZAR)</strong> unless otherwise stated.</li>
              <li>Payments are processed securely via third-party payment providers, including <strong>Adumo Online</strong>, <strong>Paystack</strong>, and <strong>PayFast</strong>. GGR does not store your card details.</li>
              <li>Subscription fees (vendor tiers, premium hosting) are billed monthly and are non-refundable once the billing cycle begins.</li>
              <li><strong>Gift-Related Refunds:</strong> For refunds on gifts purchased through the registry, please contact the event host directly. GGR facilitates the gifting process but does not handle retail refunds.</li>
              <li>GGR reserves the right to update pricing with 30 days advance notice to affected users.</li>
            </ul>
          </div>

          {/* 9a. Online Payment Gateway */}
          <h2 style={sectionTitle}>9a. Online Payment — Adumo Online Payment Gateway</h2>
          <div style={card}>
            <p style={paragraph}>
              All online credit card payments are processed by the <strong>Adumo Online Internet Payment Gateway</strong>.
              Card holders may go to <a href="https://www.adumoonline.com" target="_blank" rel="noopener noreferrer" style={{ color: '#E3425C' }}>adumoonline.com</a> to view Adumo Online's security policies.
            </p>
            <p style={paragraph}>
              GGR does not store your credit card details. All payment card information is handled exclusively by Adumo Online
              through their PCI-DSS compliant, TLS-encrypted payment page. Your card details are encrypted using 128-bit encryption
              and are never accessible to GGR.
            </p>
            <p style={paragraph}>
              <strong>Payment Security:</strong> All transactions are processed behind TLS (Transport Layer Security) security protocol.
              Adumo Online's payment page captures and encrypts your card details before transmission to the acquiring bank.
              GGR is not liable for any security breaches occurring on the user's device due to lack of adequate virus protection
              or spyware.
            </p>
            <p style={paragraph}>
              <strong>Multiple Payment Methods:</strong> Depending on availability, Adumo Online supports various payment methods
              including Credit Cards, Debit Cards, SecureEFT (instant EFT), Mobicred, Masterpass, Zapper, Apple Pay, and Google Pay.
              The available methods may vary based on the specific payment configuration.
            </p>
            <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <img src="/img/payment/adumo-logo.png" alt="Adumo Online" style={{ height: '40px' }} />
              <img src="/img/payment/visa-mc.png" alt="Visa and Mastercard" style={{ height: '30px' }} />
              <span style={{ fontSize: '12px', color: '#888' }}>Secure payments powered by Adumo Online</span>
            </div>
          </div>

          {/* 10. Platform Protections */}
          <h2 style={sectionTitle}>10. Platform Protections</h2>
          <div style={card}>
            <p style={paragraph}>
              GGR is designed to protect both hosts and vendors. When you book and pay through GGR, you receive:
            </p>
            <ul style={listStyle}>
              <li><strong>Verified Booking Records</strong> — Every booking is timestamped with details of the rate card, agreed amount, and both parties. These records are preserved and accessible as evidence in any dispute.</li>
              <li><strong>In-App Chat History</strong> — All communication between hosts and vendors is stored with timestamps and audit trails (edit history). This creates a verifiable record of what was agreed, when, and by whom.</li>
              <li><strong>Payment Verification</strong> — Payments are processed through Adumo Online and Paystack, PCI-DSS compliant payment gateways. Both parties receive payment confirmation, and GGR can verify whether payment was made.</li>
              <li><strong>Structured Cancellation</strong> — Cancellations are recorded with who cancelled, when, why, and whether a refund is due. This prevents "he said, she said" disputes.</li>
              <li><strong>Dispute Support</strong> — If a dispute arises, GGR can review booking records, chat history, and payment status to help both parties reach a fair resolution.</li>
            </ul>
            <p style={paragraph}>
              These protections only apply to bookings made and paid for through the GGR platform. Off-platform arrangements do not benefit from GGR's record-keeping, payment verification, or dispute support.
            </p>
          </div>

          {/* 11. Limitation of Liability */}
          <h2 style={sectionTitle}>11. Limitation of Liability</h2>
          <div style={card}>
            <p style={paragraph}>
              To the maximum extent permitted by South African law:
            </p>
            <ul style={listStyle}>
              <li>GGR provides the platform on an "as is" and "as available" basis without warranties of any kind, whether express or implied.</li>
              <li>GGR shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the platform.</li>
              <li>GGR is not responsible for the actions, products, or services of third-party vendors, retail stores, or payment providers.</li>
              <li>GGR does not guarantee uninterrupted or error-free service. We will make reasonable efforts to maintain uptime but accept no liability for downtime or data loss.</li>
              <li>Our total liability to any user for any claim arising from the use of GGR shall not exceed the total fees paid by that user to GGR in the 12 months preceding the claim.</li>
            </ul>
            <p style={paragraph}>
              Nothing in these terms excludes or limits liability for fraud, gross negligence, or any liability that cannot be excluded under South African law.
            </p>
          </div>

          {/* 12. Force Majeure */}
          <h2 style={sectionTitle}>12. Force Majeure</h2>
          <div style={card}>
            <p style={paragraph}>
              GGR shall not be liable for any failure or delay in performing its obligations under these Terms where such failure or delay results from circumstances beyond GGR's reasonable control ("Force Majeure Event"). Force Majeure Events include, but are not limited to:
            </p>
            <ul style={listStyle}>
              <li>Acts of God, natural disasters, floods, fires, earthquakes, or severe weather conditions</li>
              <li>Epidemics, pandemics, or public health emergencies (including government-imposed restrictions)</li>
              <li>War, terrorism, civil unrest, riots, or criminal acts of third parties</li>
              <li>National or local state of disaster declarations</li>
              <li>Load shedding, power outages, or electricity grid failures</li>
              <li>Internet or telecommunications outages affecting a significant portion of users</li>
              <li>Cyber attacks, DDoS attacks, or malicious third-party interference not caused by GGR's negligence</li>
              <li>Industrial action, strikes, or labour disputes</li>
              <li>Government action, new legislation, regulatory orders, or court orders</li>
              <li>Failure of third-party service providers essential to platform operation (payment gateways, cloud hosting, messaging APIs)</li>
            </ul>
            <p style={paragraph}>
              During a Force Majeure Event, GGR's obligations under these Terms are suspended for the duration of the event. GGR will make reasonable efforts to:
            </p>
            <ul style={listStyle}>
              <li>Notify affected users of the Force Majeure Event via email or platform notification as soon as reasonably practicable</li>
              <li>Resume normal service as soon as the Force Majeure Event ceases or is resolved</li>
              <li>Provide alternative arrangements where feasible (e.g., extended event page expiry if an event is postponed due to a Force Majeure Event)</li>
            </ul>
            <p style={paragraph}>
              This Force Majeure clause does not excuse a user's obligation to pay for services already rendered, nor does it override refund obligations required by South African consumer protection law.
            </p>
          </div>

          {/* 13. Indemnification */}
          <h2 style={sectionTitle}>13. Indemnification</h2>
          <div style={card}>
            <p style={paragraph}>
              You agree to indemnify, defend, and hold harmless Grand Gather Registry (Pty) Ltd, its officers, directors, employees, agents, and affiliates from and against any and all claims, liabilities, damages, losses, costs, and expenses (including reasonable legal fees) arising out of or in connection with:
            </p>
            <ul style={listStyle}>
              <li><strong>Your use of the platform</strong> — any activity conducted through your account, whether authorised by you or not</li>
              <li><strong>Your content</strong> — any information, images, event details, or other content you upload, post, or transmit through GGR</li>
              <li><strong>Your breach of these Terms</strong> — any violation of these Terms and Conditions or the policies incorporated by reference</li>
              <li><strong>Your violation of law</strong> — any violation of applicable South African laws or regulations, including POPIA, the Consumer Protection Act, and ECT Act</li>
              <li><strong>Disputes with other users</strong> — any dispute, claim, or disagreement between you and another user (host, vendor, or guest) arising from your use of the platform</li>
              <li><strong>Third-party claims</strong> — any claim by a third party arising from your actions on or through the platform</li>
              <li><strong>Off-platform arrangements</strong> — any transactions, bookings, or payments made outside the GGR platform, whether or not the connection was initiated through GGR</li>
            </ul>
            <p style={paragraph}>
              GGR reserves the right, at its own expense, to assume the exclusive defence and control of any matter otherwise subject to indemnification by you. In such cases, you agree to cooperate fully with GGR in asserting any available defences.
            </p>
            <p style={paragraph}>
              This indemnification provision survives the termination of your account and these Terms.
            </p>
          </div>

          {/* 14. Dispute Resolution & Escalation */}
          <h2 style={sectionTitle}>14. Dispute Resolution &amp; Escalation</h2>
          <div style={card}>
            <p style={paragraph}>
              GGR provides a structured dispute resolution process to resolve issues between users fairly, efficiently, and with clear escalation steps. All users agree to follow this process before pursuing external remedies.
            </p>

            <div style={{ padding: '20px', background: '#F8F8F8', borderRadius: '12px', marginBottom: '16px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: 700, color: C.charcoal, marginBottom: '12px' }}>Step 1 — Direct Resolution (24-48 hours)</h4>
              <p style={{ fontSize: '14px', color: '#555', lineHeight: 1.7, margin: 0 }}>
                Contact the other party through GGR's in-platform chat. Most issues are resolved at this stage through direct communication. All chat messages are timestamped and stored as evidence.
              </p>
            </div>

            <div style={{ padding: '20px', background: '#F8F8F8', borderRadius: '12px', marginBottom: '16px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: 700, color: C.charcoal, marginBottom: '12px' }}>Step 2 — Platform Support (Response within 24 hours)</h4>
              <p style={{ fontSize: '14px', color: '#555', lineHeight: 1.7, margin: 0 }}>
                If direct resolution fails, contact GGR support at <strong>support@grandgatherregistry.co.za</strong> or via WhatsApp at <strong>+27 61 391 1942</strong>. Provide your booking reference, event details, and a description of the issue. GGR will acknowledge your complaint within 24 hours.
              </p>
            </div>

            <div style={{ padding: '20px', background: '#F8F8F8', borderRadius: '12px', marginBottom: '16px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: 700, color: C.charcoal, marginBottom: '12px' }}>Step 3 — Formal Investigation (7 business days)</h4>
              <p style={{ fontSize: '14px', color: '#555', lineHeight: 1.7, margin: 0 }}>
                GGR's dispute resolution team will formally investigate your case by reviewing:
              </p>
              <ul style={{ ...listStyle, marginTop: '8px' }}>
                <li>Booking records, rate cards, and payment confirmations</li>
                <li>Chat message history (including edit history) between the parties</li>
                <li>Evidence submitted by both parties (photos, receipts, delivery confirmations)</li>
                <li>Vendor service history and host event records</li>
              </ul>
              <p style={{ fontSize: '14px', color: '#555', lineHeight: 1.7, margin: '8px 0 0 0' }}>
                A formal resolution will be issued within <strong>7 business days</strong>, which may include: full refund, partial refund, replacement service, or dismissal of the claim with reasons.
              </p>
            </div>

            <div style={{ padding: '20px', background: '#F8F8F8', borderRadius: '12px', marginBottom: '16px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: 700, color: C.charcoal, marginBottom: '12px' }}>Step 4 — Appeal (5 business days)</h4>
              <p style={{ fontSize: '14px', color: '#555', lineHeight: 1.7, margin: 0 }}>
                If you are dissatisfied with the formal investigation outcome, you may file an appeal by emailing <strong>appeals@grandgatherregistry.co.za</strong> with new evidence or explaining why you believe the decision was incorrect. Appeals are reviewed by a senior team member not involved in the original decision, and a final determination is issued within <strong>5 business days</strong>.
              </p>
            </div>

            <div style={{ padding: '20px', background: '#FFF5F6', borderRadius: '12px', border: '1px solid rgba(227,66,92,0.15)' }}>
              <h4 style={{ fontSize: '16px', fontWeight: 700, color: C.charcoal, marginBottom: '12px' }}>Step 5 — External Escalation</h4>
              <p style={{ fontSize: '14px', color: '#555', lineHeight: 1.7, margin: 0 }}>
                If you have exhausted GGR's internal dispute resolution process and remain unsatisfied, the following external avenues are available:
              </p>
              <ul style={{ ...listStyle, marginTop: '8px' }}>
                <li><strong>Adumo Online & Paystack Dispute Resolution</strong> — For payment-related disputes, Adumo Online and Paystack's chargeback and dispute resolution processes are available as provided by their respective terms</li>
                <li><strong>Consumer Goods & Services Ombud (CGSO)</strong> — For complaints about goods or services, you may approach the CGSO under the Consumer Protection Act</li>
                <li><strong>National Consumer Commission (NCC)</strong> — For complaints about prohibited conduct or violations of consumer rights under the CPA</li>
                <li><strong>Information Regulator of South Africa</strong> — For complaints about personal information processing or POPIA violations</li>
                <li><strong>Small Claims Court</strong> — For claims within the monetary jurisdiction of the Small Claims Court (currently R20,000)</li>
                <li><strong>South African Courts</strong> — As a final resort, disputes may be referred to the courts of the Republic of South Africa</li>
              </ul>
            </div>
          </div>

          {/* 15. Reporting Issues */}
          <h2 style={sectionTitle}>15. Reporting Issues</h2>
          <div style={card}>
            <p style={paragraph}>
              If you experience a problem with a booking, vendor, or host — or if you need GGR to review a dispute — contact us at info@grandgatherregistry.co.za with your booking details. GGR will review the booking records, chat history, and payment status to assist in reaching a resolution.
            </p>
          </div>

          {/* 16. Additional Policies */}
          <h2 style={sectionTitle}>16. Additional Policies</h2>
          <div style={card}>
            <p style={paragraph}>
              The following policies form part of these Terms and Conditions. By using GGR, you also agree to be bound by:
            </p>
            <ul style={listStyle}>
              <li><strong><Link to="/acceptable-use" style={{ color: C.coral, textDecoration: 'none' }}>Acceptable Use Policy</Link></strong> — Rules for appropriate conduct on the platform, including prohibited content, anti-harassment rules, and account suspension criteria.</li>
              <li><strong><Link to="/shipping-policy" style={{ color: C.coral, textDecoration: 'none' }}>Shipping & Delivery Policy</Link></strong> — How physical products (gifts, cakes, flowers, printed materials) are shipped and delivered by vendors, including timeframes and responsibilities.</li>
              <li><strong><Link to="/returns-policy" style={{ color: C.coral, textDecoration: 'none' }}>Returns & Refunds Policy</Link></strong> — Cancellation, return, and refund procedures for service vendors, product vendors, gift registry contributions, subscriptions, and event tickets.</li>
              <li><strong><Link to="/privacy" style={{ color: C.coral, textDecoration: 'none' }}>Privacy Policy</Link></strong> — How we collect, use, and protect your personal data in compliance with POPIA.</li>
              <li><strong><Link to="/cookies" style={{ color: C.coral, textDecoration: 'none' }}>Cookie Policy</Link></strong> — Information about cookies used on our platform and your choices.</li>
            </ul>
            <p style={paragraph}>
              These policies are incorporated by reference into these Terms and Conditions. In the event of any conflict between these Terms and the specific policy, the specific policy shall prevail with respect to its subject matter.
            </p>
          </div>

          {/* 17. Contact */}
          <h2 style={sectionTitle}>17. Contact</h2>
          <div style={card}>
            <p style={paragraph}>
              If you have questions, concerns, or requests regarding these Terms and Conditions, please contact us:
            </p>
            <div style={{ padding: '20px', background: '#F8F8F8', borderRadius: '12px', marginTop: '12px' }}>
              <p style={{ fontSize: '15px', color: C.charcoal, lineHeight: 1.9, margin: 0 }}>
                <strong>Grand Gather Registry (Pty) Ltd</strong><br />
                Registration Number: 2025/182063/07<br />
                South Africa (Online)<br /><br />
                Email: <a href="mailto:info@grandgatherregistry.co.za" style={{ color: C.coral, textDecoration: 'none', fontWeight: 500 }}>info@grandgatherregistry.co.za</a><br />
                Phone: <a href="https://wa.me/27613911942" style={{ color: C.coral, textDecoration: 'none', fontWeight: 500 }}>+27 61 391 1942</a><br />
                Website: <a href="https://grandgatherregistry.co.za" style={{ color: C.coral, textDecoration: 'none', fontWeight: 500 }}>grandgatherregistry.co.za</a>
              </p>
            </div>
          </div>

          {/* 18. Governing Law */}
          <h2 style={sectionTitle}>18. Governing Law</h2>
          <div style={card}>
            <p style={paragraph}>
              These Terms and Conditions are governed by and construed in accordance with the laws of the <strong>Republic of South Africa</strong>. Any disputes arising from or in connection with these terms shall be subject to the exclusive jurisdiction of the courts of South Africa.
            </p>
            <p style={paragraph}>
              In compliance with the <strong>Electronic Communications and Transactions Act 25 of 2002 (ECT Act)</strong>, GGR provides the following information to consumers:
            </p>
            <ul style={listStyle}>
              <li><strong>Full name and legal status:</strong> Grand Gather Registry (Pty) Ltd, a private company duly incorporated under the laws of South Africa.</li>
              <li><strong>Registration number:</strong> 2025/182063/07</li>
              <li><strong>Place of registration:</strong> Companies and Intellectual Property Commission (CIPC), South Africa.</li>
              <li><strong>Physical address:</strong> Available on request via email.</li>
              <li><strong>Website:</strong> grandgatherregistry.co.za</li>
              <li><strong>Email:</strong> info@grandgatherregistry.co.za</li>
              <li><strong>Description of services:</strong> AI-powered event management and gift registry platform connecting event hosts, guests, and vendors.</li>
              <li><strong>Cooling-off period:</strong> Consumers may cancel electronic transactions within 7 days of conclusion without reason or penalty, subject to the exclusions in section 42(2) of the ECT Act.</li>
              <li><strong>Complaint resolution:</strong> Complaints may be submitted to info@grandgatherregistry.co.za and will be acknowledged within 7 days and resolved within 30 days.</li>
            </ul>
          </div>

          {/* 19. Effective Date */}
          <h2 style={sectionTitle}>19. Effective Date</h2>
          <div style={card}>
            <p style={paragraph}>
              These Terms and Conditions are effective as of <strong>June 2026</strong> and apply to all users of the Grand Gather Registry platform from that date forward.
            </p>
            <p style={paragraph}>
              Last updated: June 2026
            </p>
          </div>

          {/* Back link */}
          <div style={{ textAlign: 'center', marginTop: '48px' }}>
            <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 28px', borderRadius: '10px', background: C.coral, color: 'white', textDecoration: 'none', fontSize: '14px', fontWeight: 600 }}>
              <FiArrowLeft size={15} /> Back to Home
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ padding: '40px 0 24px', background: C.charcoal, marginTop: '40px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 28px', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <Link to="/about" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}>About</Link>
            <Link to="/contact" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}>Contact</Link>
            <Link to="/help" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}>Help</Link>
            <Link to="/terms" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none', fontWeight: 600 }}>Terms &amp; Conditions</Link>
            <Link to="/privacy" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}>Privacy</Link>
            <Link to="/acceptable-use" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}>Acceptable Use</Link>
            <Link to="/shipping-policy" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}>Shipping</Link>
            <Link to="/returns-policy" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)', textDecoration: 'none' }}>Returns</Link>
          </div>
          <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.25)' }}>
            &copy; 2025–2026 Grand Gather Registry (Pty) Ltd. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
